'use client'
import { useEffect } from 'react'
import * as amplitude from '@amplitude/analytics-browser'
import {
	pushUserDetails,
	initAmplitudeOnce,
} from '@/app/lib/analytics/analyticsTracking'
import { bootAmplitudeEngagement } from '@/app/lib/analytics/amplitudeGuidesManager'
import { extractErrorOrUnknown } from '@/app/lib/errors/errorUtils'
import { useSession } from 'next-auth/react'
import { useUserContext } from '@/lib/context/userContext'
import { useCompanyContext } from '@/lib/context/companyContext'

export default function PushUserDetails() {
	const { data: session } = useSession()
	const { repId } = useUserContext()
	const { selectedCompanyId: companyId, brandCode, affiliationTypeCd } = useCompanyContext()
	const oktaId = session?.oktaId || null

	useEffect(() => {
		const pushUserDetailsFunc = async () => {
			if (oktaId) {
				try {
					// 0. Ensure Amplitude Core is initialized
					await initAmplitudeOnce()

					// 1. Boot Amplitude Guides & Surveys FIRST
					// Pass the device ID that the core SDK generated so all three systems
					// (analytics, session replay, engagement) share the same device ID.
					await bootAmplitudeEngagement({
						user_id: oktaId,
						device_id: amplitude.getDeviceId(),
						user_properties: {
							companyId: companyId ?? '',
							brandCode: brandCode ?? '',
							affiliationTypeCd,
							repId: repId ?? '',
						},
					})

					// 2. Push user identity to Amplitude Analytics
					await pushUserDetails(
						oktaId,
						companyId ?? '',
						brandCode ?? '',
						affiliationTypeCd,
						repId
					)
				} catch (error) {
					// Extract error details using established pattern
					const normalized = extractErrorOrUnknown(
						error,
						'Failed to initialize user analytics',
						'User analytics initialization failed',
						'USER_ANALYTICS'
					)

					// Log error with context for debugging
					console.warn(
						'User analytics initialization warning:',
						normalized.message
					)
				}
			}
		}
		pushUserDetailsFunc()
		// Dependencies: only re-run when user context changes (companyId, oktaId, brandCode)
		// Not including pathName - it causes unnecessary re-runs on every navigation
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, oktaId, brandCode])

	return null
}
